import React, { useId, useState } from "react";

import { cn } from "../../../common/u1/cn";
import { CompanyMark } from "../../../common/u1/CompanyMark";
import { ChevronDown } from "../../../../assets/ui/icons";
import { TrackedStatusBadge } from "./TrackedStatusBadge";
import { ProfileCompletionBar } from "./ProfileCompletionBar";
import { TrackedUpdatesPanel } from "./TrackedUpdatesPanel";
import {
  trackedAddress,
  trackedChanges,
  trackedProfileId,
  trackedStatus,
} from "./enterpriseRows";

/* The tracked buyers, as cards — the phone's shape for the same five columns. */

const ROW = "flex items-start justify-between gap-3 border-t border-[var(--line)] py-2.5";
/* Not shrink-0: on a narrow card the label wraps rather than pushing the value
   past the card's edge. */
const LABEL =
  "min-w-0 text-[10.5px] font-bold uppercase tracking-[.07em] text-[var(--muted)] font-sans";
const VALUE = "min-w-0 text-right text-[13px] text-[var(--ink-2)] font-sans";

export const TrackedBusinessCard = ({
  row,
  onOpenProfile = () => {},
  onOpenChanges = () => {},
  onCloseChanges = () => {},
}) => {
  const profileId = trackedProfileId(row);
  const changes = trackedChanges(row);
  const address = trackedAddress(row);
  const status = trackedStatus(row);
  const [open, setOpen] = useState(false);
  const panelId = `${useId()}-updates`;

  /* Identical to the table row's: opening marks the changes seen, closing
     refreshes the list — a refetch while the panel is open would empty it
     under the reader. */
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) onOpenChanges(row);
    else onCloseChanges(row);
  };

  return (
    <article className="rounded-[14px] border border-[var(--line)] bg-white p-4">
      {/* The business, and where it trades from — the design puts the place
          and the badge on the right of this row. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="shrink-0">
            <CompanyMark
              company={{
                name: row?.company_name,
                logo: row?.logo,
                companyID: profileId,
              }}
              member={Boolean(row?.verified)}
              size="xs"
              tone="brand"
            />
          </span>

          {/* Transcribed: only a business with an id behind it can be opened,
              and the unopenable ones are plainly inert. */}
          {profileId ? (
            <button
              type="button"
              onClick={() => onOpenProfile(profileId)}
              className={cn(
                "min-w-0 truncate text-left text-[14px] font-bold text-[var(--ink)] font-sans",
                "transition-colors hover:text-[var(--coral)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]"
              )}
            >
              {row?.company_name}
            </button>
          ) : (
            <span
              className="min-w-0 truncate text-[14px] font-bold text-[var(--ink)] font-sans"
              title="This business has no profile to open yet"
            >
              {row?.company_name}
            </span>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span className="text-right text-[12px] text-[var(--ink-2)] font-sans">
            {address}
          </span>
          <TrackedStatusBadge status={status} />
        </div>
      </div>

      <div className="mt-3">
        <div className={ROW}>
          <span className={LABEL}>Address</span>
          <span className={VALUE}>{address}</span>
        </div>

        <div className={cn(ROW, "items-center")}>
          <span className={LABEL}>Status</span>
          <TrackedStatusBadge status={status} />
        </div>

        <div className={cn(ROW, "items-center")}>
          <span className={LABEL}>Recent changes</span>
          {changes.actionable ? (
            <button
              type="button"
              onClick={toggle}
              aria-expanded={open}
              aria-controls={panelId}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 text-[13px] font-semibold",
                "text-[var(--warning)] font-sans transition-colors hover:text-[var(--coral)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]"
              )}
            >
              <span
                aria-hidden="true"
                className="h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--warning)]"
              />
              {changes.label}
              <span className="shrink-0">
                <ChevronDown open={open} />
              </span>
            </button>
          ) : (
            <span className="inline-flex shrink-0 items-center gap-1.5 text-[13px] text-[var(--muted)] font-sans">
              <span
                aria-hidden="true"
                className="h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--muted-2)]"
              />
              {changes.label}
            </span>
          )}
        </div>

        <div className={cn(ROW, "items-center")}>
          <span className={LABEL}>Profile completion</span>
          {/* The compact meter keeps its own width — bar AND figure both stay legible — and the label beside it wraps if the card is too narrow for one line. */}
          <ProfileCompletionBar percent={row?.percent} size="sm" className="shrink-0" />
        </div>
      </div>

      {open && (
        <div className="mt-3 overflow-hidden rounded-xl border border-[var(--line)]">
          <TrackedUpdatesPanel id={panelId} updates={row?.updates} />
        </div>
      )}
    </article>
  );
};

export const TrackedBusinessCardList = ({
  rows = [],
  onOpenProfile,
  onOpenChanges,
  onCloseChanges,
  emptyMessage = "No businesses to show.",
  className = "",
}) => (
  <div className={cn("flex flex-col gap-3 px-4 pb-4", className)}>
    {rows.length === 0 ? (
      <p className="m-0 py-8 text-center text-[13.5px] text-[var(--muted)] font-sans">
        {emptyMessage}
      </p>
    ) : (
      rows.map((row, i) => (
        <TrackedBusinessCard
          /* Tracked rows carry no stable id of their own in every shape, so
             the company id leads and the index is the fallback. */
          key={trackedProfileId(row) || `tracked-card-${i}`}
          row={row}
          onOpenProfile={onOpenProfile}
          onOpenChanges={onOpenChanges}
          onCloseChanges={onCloseChanges}
        />
      ))
    )}
  </div>
);

export default TrackedBusinessCardList;
